import React, { FC, useState } from 'react';
import { Editor } from '@tiptap/react';
import {
  Bold,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Underline as UnderlineIcon,
} from 'lucide-react';
import { Button } from '@components/atoms/Button';
import { Input } from '@components/atoms/Input';
import { cn } from '@utils/cn';

interface ToolbarProps {
  editor: Editor;
  disabled?: boolean;
}

interface ToolButton {
  key: string;
  label: string;
  icon: React.ReactNode;
  isActive: () => boolean;
  run: () => void;
}

/**
 * Toolbar for RichTextEditor. Private to the atom — it is never exported on its own, because
 * it is meaningless without the editor instance it drives.
 *
 * Every button is `type="button"`. The editor renders inside CreateEditTaskModal's <form>, and
 * a bare <button> defaults to type="submit", so omitting this would make bolding a word submit
 * the task.
 */
export const Toolbar: FC<ToolbarProps> = ({ editor, disabled = false }) => {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkValue, setLinkValue] = useState('');

  const buttons: ToolButton[] = [
    {
      key: 'bold',
      label: 'Bold',
      icon: <Bold className="w-4 h-4" />,
      isActive: () => editor.isActive('bold'),
      run: () => editor.chain().focus().toggleBold().run(),
    },
    {
      key: 'italic',
      label: 'Italic',
      icon: <Italic className="w-4 h-4" />,
      isActive: () => editor.isActive('italic'),
      run: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      key: 'underline',
      label: 'Underline',
      icon: <UnderlineIcon className="w-4 h-4" />,
      isActive: () => editor.isActive('underline'),
      run: () => editor.chain().focus().toggleUnderline().run(),
    },
    {
      key: 'h1',
      label: 'Heading 1',
      icon: <Heading1 className="w-4 h-4" />,
      isActive: () => editor.isActive('heading', { level: 1 }),
      run: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
    },
    {
      key: 'h2',
      label: 'Heading 2',
      icon: <Heading2 className="w-4 h-4" />,
      isActive: () => editor.isActive('heading', { level: 2 }),
      run: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      key: 'h3',
      label: 'Heading 3',
      icon: <Heading3 className="w-4 h-4" />,
      isActive: () => editor.isActive('heading', { level: 3 }),
      run: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
    },
    {
      key: 'bulletList',
      label: 'Bulleted list',
      icon: <List className="w-4 h-4" />,
      isActive: () => editor.isActive('bulletList'),
      run: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      key: 'orderedList',
      label: 'Numbered list',
      icon: <ListOrdered className="w-4 h-4" />,
      isActive: () => editor.isActive('orderedList'),
      run: () => editor.chain().focus().toggleOrderedList().run(),
    },
    {
      key: 'blockquote',
      label: 'Quote',
      icon: <Quote className="w-4 h-4" />,
      isActive: () => editor.isActive('blockquote'),
      run: () => editor.chain().focus().toggleBlockquote().run(),
    },
  ];

  const openLinkEditor = () => {
    setLinkValue((editor.getAttributes('link').href as string) || '');
    setLinkOpen((open) => !open);
  };

  const applyLink = () => {
    const href = linkValue.trim();
    if (!href) {
      editor.chain().focus().unsetLink().run();
    } else {
      // Default to https so a pasted "example.com" does not become a relative link.
      const normalized = /^(https?:|mailto:|tel:)/i.test(href) ? href : `https://${href}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href: normalized }).run();
    }
    setLinkOpen(false);
  };

  const removeLink = () => {
    editor.chain().focus().unsetLink().run();
    setLinkValue('');
    setLinkOpen(false);
  };

  return (
    <div className="border-b border-gray-300 dark:border-gray-600">
      <div className="flex flex-wrap items-center gap-1 p-1.5">
        {buttons.map((button) => (
          <Button
            key={button.key}
            type="button"
            variant="tertiary"
            size="md"
            onlyIcon
            leftIcon={button.icon}
            isSelected={button.isActive()}
            disabled={disabled}
            title={button.label}
            aria-label={button.label}
            aria-pressed={button.isActive()}
            onMouseDown={(event) => event.preventDefault()}
            onClick={button.run}
          />
        ))}

        <Button
          type="button"
          variant="tertiary"
          size="md"
          onlyIcon
          leftIcon={<LinkIcon className="w-4 h-4" />}
          isSelected={editor.isActive('link') || linkOpen}
          disabled={disabled}
          title="Link"
          aria-label="Link"
          aria-pressed={editor.isActive('link')}
          onMouseDown={(event) => event.preventDefault()}
          onClick={openLinkEditor}
        />
      </div>

      {/*
        An inline row rather than window.prompt(): a native prompt is a blocking modal dialog,
        which is hostile inside a slide-over and impossible to drive in browser tests.
      */}
      {linkOpen && (
        <div
          className={cn(
            'flex items-center gap-2 px-1.5 pb-1.5',
            'border-t border-gray-200 dark:border-gray-700 pt-1.5',
          )}>
          <Input
            // eslint-disable-next-line jsx-a11y/no-autofocus -- the row only exists once the
            // user has clicked the link button, so focusing it is the expected next step.
            autoFocus
            type="url"
            value={linkValue}
            placeholder="https://example.com"
            className="flex-1 text-sm"
            onChange={(event) => setLinkValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                applyLink();
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                setLinkOpen(false);
              }
            }}
          />
          <Button type="button" variant="secondary" size="md" onClick={applyLink}>
            Apply
          </Button>
          <Button type="button" variant="tertiary" size="md" onClick={removeLink}>
            Remove
          </Button>
        </div>
      )}
    </div>
  );
};

export default Toolbar;
